/**
 * Invite Coach Dialog
 * Modal for inviting new coaches
 */
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Loader2, Send, Shield, DollarSign } from "lucide-react";

export const InviteCoachDialog = ({
  open,
  onOpenChange,
  inviteForm,
  setInviteForm,
  saving,
  onSave
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Invite a Coach</DialogTitle>
          <DialogDescription>Send an invitation email to join your organization as a coach.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-4 py-4 pr-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>First Name *</Label>
              <Input 
                value={inviteForm.first_name} 
                onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })} 
                className="mt-1" 
              />
            </div>
            <div>
              <Label>Last Name</Label>
              <Input 
                value={inviteForm.last_name} 
                onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })} 
                className="mt-1" 
              />
            </div>
          </div>
          <div>
            <Label>Email *</Label>
            <Input 
              type="email" 
              value={inviteForm.email} 
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} 
              className="mt-1" 
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input 
              type="tel" 
              value={inviteForm.phone} 
              onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })} 
              className="mt-1" 
            />
          </div>
          <div>
            <Label>Personal Message</Label>
            <Textarea 
              value={inviteForm.message} 
              onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })} 
              placeholder="Welcome to our team!" 
              className="mt-1" 
            />
          </div>
          
          {/* Background Check Option */}
          <div className="border border-slate-200 rounded-xl p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Checkbox
                id="require-background-check"
                checked={inviteForm.require_background_check || false}
                onCheckedChange={(checked) => 
                  setInviteForm({ ...inviteForm, require_background_check: checked })
                }
                data-testid="require-background-check"
              />
              <div className="grid gap-1.5 leading-none">
                <label
                  htmlFor="require-background-check"
                  className="text-sm font-medium text-slate-900 cursor-pointer"
                >
                  Require Background Check
                </label>
                <p className="text-xs text-slate-500">
                  Coach will need to complete a background check before approval
                </p>
              </div>
            </div>
            {inviteForm.require_background_check && (
              <>
                <div className="bg-slate-50 rounded-lg p-3 space-y-3">
                  <p className="text-sm font-medium text-slate-700">Who pays for the background check?</p>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="bg-check-payer"
                        value="coach"
                        checked={inviteForm.background_check_paid_by === "coach"}
                        onChange={() => setInviteForm({ ...inviteForm, background_check_paid_by: "coach" })}
                        className="w-4 h-4 text-indigo-600"
                      />
                      <span className="text-sm text-slate-700">Coach pays</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="bg-check-payer"
                        value="organization"
                        checked={inviteForm.background_check_paid_by === "organization"}
                        onChange={() => setInviteForm({ ...inviteForm, background_check_paid_by: "organization" })}
                        className="w-4 h-4 text-indigo-600"
                      />
                      <span className="text-sm text-slate-700">Organization pays</span>
                    </label>
                  </div>
                </div>
                <div className={`rounded-lg p-3 ${inviteForm.background_check_paid_by === "organization" ? "bg-indigo-50 border border-indigo-200" : "bg-amber-50 border border-amber-200"}`}>
                  <div className="flex items-start gap-2">
                    <DollarSign className={`w-4 h-4 mt-0.5 ${inviteForm.background_check_paid_by === "organization" ? "text-indigo-600" : "text-amber-600"}`} />
                    <div className="text-xs">
                      <p className={`font-medium ${inviteForm.background_check_paid_by === "organization" ? "text-indigo-800" : "text-amber-800"}`}>
                        Background Check Fee: $39.99
                      </p>
                      <p className={`mt-0.5 ${inviteForm.background_check_paid_by === "organization" ? "text-indigo-600" : "text-amber-600"}`}>
                        {inviteForm.background_check_paid_by === "organization" 
                          ? "Your organization will be billed by Sporthouse"
                          : "The coach will pay during registration"}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          
        </div>
        <DialogFooter className="flex-shrink-0 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-slate-900">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send Invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteCoachDialog;
